import { forwardRef, useId, useState, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../lib/cn';

interface FieldShell {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Slot on the right of the label row, e.g. a "forgot password" link. */
  aside?: ReactNode;
  /** Keep the label for screen readers but hide it visually (still self-explanatory
   *  fields like a search box with a placeholder). */
  hideLabel?: boolean;
}

const controlBase =
  'w-full rounded-control bg-elevated border px-4 text-ink placeholder:text-muted ' +
  'transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed';

function shell(error?: string) {
  return cn(controlBase, error ? 'border-bad focus:border-bad' : 'border-line focus:border-ink-soft');
}

function Labelled({
  label,
  hint,
  error,
  required,
  aside,
  hideLabel,
  htmlFor,
  children,
}: FieldShell & { htmlFor: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      {hideLabel ? (
        <label htmlFor={htmlFor} className="sr-only">
          {label}
        </label>
      ) : (
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor={htmlFor} className="text-sm font-medium text-ink-soft">
            {label}
            {required && <span className="ml-0.5 text-bad">*</span>}
          </label>
          {aside}
        </div>
      )}
      {children}
      {error ? (
        <p className="text-xs text-bad" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement>, FieldShell {
  /** Leading element (icon or symbol like "$"). */
  leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, aside, hideLabel, leading, className, id, type, ...props },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);
  return (
    <Labelled label={label} hint={hint} error={error} required={required} aside={aside} hideLabel={hideLabel} htmlFor={fieldId}>
      <div className="relative flex items-center">
        {leading && <span className="pointer-events-none absolute left-4 text-muted">{leading}</span>}
        <input
          ref={ref}
          id={fieldId}
          type={isPassword && revealed ? 'text' : type}
          required={required}
          aria-invalid={error ? true : undefined}
          className={cn(shell(error), 'h-touch', leading && 'pl-9', isPassword && 'pr-11', className)}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            className="absolute right-2.5 inline-flex size-8 items-center justify-center rounded-control text-muted transition-colors hover:text-ink"
          >
            {revealed ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
          </button>
        )}
      </div>
    </Labelled>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FieldShell {}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, aside, hideLabel, className, id, children, ...props },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Labelled label={label} hint={hint} error={error} required={required} aside={aside} hideLabel={hideLabel} htmlFor={fieldId}>
      <select
        ref={ref}
        id={fieldId}
        required={required}
        aria-invalid={error ? true : undefined}
        className={cn(shell(error), 'h-touch appearance-none bg-[url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%237E8475\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'><polyline points=\'6 9 12 15 18 9\'/></svg>")] bg-[right_1rem_center] bg-no-repeat pr-10', className)}
        {...props}
      >
        {children}
      </select>
    </Labelled>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldShell {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, aside, className, id, ...props },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Labelled label={label} hint={hint} error={error} required={required} aside={aside} htmlFor={fieldId}>
      <textarea
        ref={ref}
        id={fieldId}
        required={required}
        aria-invalid={error ? true : undefined}
        className={cn(shell(error), 'min-h-24 resize-y py-3', className)}
        {...props}
      />
    </Labelled>
  );
});
